import React, { useState } from 'react';
import { useAuth } from './AuthProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sanitizeNextPath } from '../utils/detailRoute';
import {
    Alert,
    Container,
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Link,
    IconButton,
    InputAdornment,
    Tooltip
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import KeyIcon from '@mui/icons-material/Key';
import { useAlert } from './AlertContext';
import { useAuthenticateWithPasskey } from '../hooks/webauthn';
import { isWebAuthnSecureContext, webAuthnDisabledReason } from '../utils/webauthn';
import { cardLgSx, fieldSx, primaryButtonSx, secondaryButtonSx } from './pvtaStyles';

const Login = () => {
    const { login, applyAuthSession, authError } = useAuth();
    const { showAlert } = useAlert();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const webAuthnEnabled = isWebAuthnSecureContext();
    const authenticateWithPasskey = useAuthenticateWithPasskey();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Ziel eines Deep-Links, den ein nicht angemeldeter Nutzer aufgerufen hat
    // (z. B. `/events/42` aus einer Mail). `sanitizeNextPath` laesst ausschliesslich
    // interne Pfade durch, damit `?next=` kein Open-Redirect wird.
    const redirectTarget = sanitizeNextPath(searchParams.get('next')) ?? '/dashboard';

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        try {
            await login({ email: email, password });
            navigate(redirectTarget, { replace: true });
        } catch {
        } finally {
            setLoading(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword((show) => !show);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handlePasskeyLogin = async () => {
        try {
            const response = await authenticateWithPasskey.mutateAsync({
                username: email.trim() || undefined,
            });
            applyAuthSession(response);
            showAlert({ severity: 'success', text: 'Passkey-Anmeldung erfolgreich.' });
            navigate(redirectTarget, { replace: true });
        } catch (error: unknown) {
            const text = error instanceof Error && error.message.trim()
                ? error.message
                : 'Passkey-Anmeldung fehlgeschlagen';
            showAlert({ severity: 'error', text });
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(160deg, var(--pvta-bg) 0%, var(--pvta-green-100) 100%)',
            }}
        >
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', px: { xs: 0, sm: 2 } }}>
                <Paper
                    elevation={0}
                    sx={{ ...cardLgSx, p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 420, boxSizing: 'border-box' }}
                >
                    <Box display="flex" justifyContent="center" mb={3}>
                        <img
                            src="/icons/pvta-logo.svg"
                            alt="PV! Teutonia-Alemannia"
                            style={{ maxWidth: 160, height: 'auto' }}
                        />
                    </Box>
                    <Typography
                        align="center"
                        sx={{ fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 700, fontSize: 26, color: 'var(--pvta-green-900)', mb: 0.5 }}
                    >
                        Login
                    </Typography>
                    <Typography variant="body2" align="center" sx={{ color: 'var(--pvta-muted)', mb: 2 }}>
                        Willkommen im Mitgliederbereich der PV! Teutonia-Alemannia.
                    </Typography>
                    {!webAuthnEnabled && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {webAuthnDisabledReason}
                        </Alert>
                    )}
                    <form onSubmit={handleSubmit}>
                        <TextField
                            label="E-Mail"
                            type="email"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            size="small"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            sx={{ ...fieldSx }}
                        />
                        <TextField
                            label="Passwort"
                            variant="outlined"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            margin="normal"
                            size="small"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            sx={{ ...fieldSx }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            disabled={loading || authenticateWithPasskey.isPending}
                            sx={{ ...primaryButtonSx, mt: 2 }}
                        >
                            {loading ? 'Wird eingeloggt...' : 'Einloggen'}
                        </Button>
                        <Tooltip title={!webAuthnEnabled ? webAuthnDisabledReason : ''}>
                            <span>
                                <Button
                                    type="button"
                                    fullWidth
                                    startIcon={<KeyIcon />}
                                    onClick={handlePasskeyLogin}
                                    disabled={!webAuthnEnabled || loading || authenticateWithPasskey.isPending}
                                    sx={{ ...secondaryButtonSx, mt: 1.5 }}
                                >
                                    {authenticateWithPasskey.isPending ? 'Passkey wird geprüft...' : 'Mit Passkey anmelden'}
                                </Button>
                            </span>
                        </Tooltip>
                        {authError && (
                            <Typography variant="body2" color="error" align="center" sx={{ mt: 2 }}>
                                Error: {authError.toString()}
                            </Typography>
                        )}
                        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={() => navigate('/forgot-password')}
                                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                            >
                                Passwort vergessen?
                            </Link>
                        </Typography>
                    </form>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;
